/**
 * `isUserAbort` — detect a user-driven upload cancel inside a wire's catch.
 *
 * Why: when the user clicks "cancel" in the tray, the store's `cancel(id)`
 * synchronously aborts the per-upload `AbortController` and calls
 * `markCancelled` (status='cancelled'). Axios's in-flight POST then rejects
 * with a `CanceledError`. The wire's catch block must NOT treat that
 * rejection as a real failure — that would still increment `errored`
 * counters or toast "1 upload failed" for what the user just cancelled.
 *
 * Cancel is detected PURELY by error SHAPE — the `AbortSignal` state is
 * intentionally IGNORED, which is why this function takes no `signal`
 * parameter.
 * Rationale: a real 5xx (or network drop) can race a user cancel and
 * arrive in the same tick that `signal.aborted` flips to `true`. If we
 * used `signal.aborted` as the cancel verdict we would SWALLOW that real
 * failure — the wire would silently classify it as "user cancelled" and
 * skip its tally / toast / log paths. By keying off the error shape
 * (`axios.isCancel(err)` or `err.name === 'AbortError' | 'CanceledError'`)
 * we only treat as cancel what axios / the DOM actually produced from the
 * abort itself; everything else stays a real failure. The cancel-race
 * scenario for the UI side is handled separately by the store's
 * terminal-state guard: `markFailed` on an item
 * already in `cancelled` no-ops the status, so debuggability of the real
 * error is preserved WITHOUT regressing the cancelled UI label.
 *
 * Detection (cancel requires a cancel-SHAPED error):
 *   1. `axios.isCancel(err)` matches `axios.Cancel` / `CanceledError`
 *      from both axios <1.x and >=1.x runtimes.
 *   2. `err.name === 'AbortError'` (DOM) or `'CanceledError'` (axios
 *      >=1.x). Uses a duck-typed guard (NOT `instanceof Error`) so
 *      plain objects from older xhr shims are also matched.
 *
 * Anything else (network, 4xx, 5xx, server) is a real failure regardless
 * of the AbortSignal state.
 */
import axios from 'axios';

function hasCancelName(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const name = (err as { name?: unknown }).name;
  return name === 'AbortError' || name === 'CanceledError';
}

export function isUserAbort(err: unknown): boolean {
  if (axios.isCancel(err)) return true;
  if (hasCancelName(err)) return true;
  return false;
}
